import { Controller, Form, FormProvider } from 'react-hook-form';
import { capitalize, isNil } from 'lodash';
import { Box, Button, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useCreatePartner } from './hooks';
import { EMR_TYPE } from '@common/types';
import { t } from 'i18next';
import { USStateSelect } from 'components/USStateSelect';
import {
  CustomModal,
  FileUploader,
  FormGooglePlacesAutocomplete,
  SaveChangesModal,
} from '@components';
import { Select } from 'ui';

export const CreatePartner = ({
  handleOpenPartnerDetails,
}: {
  handleOpenPartnerDetails: (id: string) => void;
}) => {
  const {
    formMethods,
    handleClose,
    handleOnClick,
    isOpen,
    isSubmitting,
    onSubmit,
    organizationOptions,
    emrValue,
    handleSelectLocation,
    file,
    handleChangeFile,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useCreatePartner(handleOpenPartnerDetails);

  const { control } = formMethods;

  return (
    <>
      <Button
        startIcon={<AddIcon />}
        sx={{ fontWeight: 'regular', fontSize: '1rem', color: 'white' }}
        color='secondary'
        variant='contained'
        onClick={handleOnClick}
      >
        {t('adminPanel.partners.addModal.addPartner')}
      </Button>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('adminPanel.partners.addModal.title')}
        isLoading={isSubmitting}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Controller
              name='name'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.partners.addModal.name')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name='organization'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  label={t('adminPanel.partners.addModal.organization')}
                  error={!isNil(error)}
                  required
                  options={organizationOptions}
                  helperText={error?.message}
                />
              )}
            />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='emr'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    label={t('adminPanel.partners.addModal.emr')}
                    error={!isNil(error)}
                    required
                    options={Object.values(EMR_TYPE).map(o => ({
                      value: o,
                      label: o
                        .split('_')
                        .map(token => capitalize(token))
                        .join(' '),
                    }))}
                    helperText={error?.message}
                  />
                )}
              />

              {emrValue === EMR_TYPE.OTHER && (
                <Controller
                  name='emr_other'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type='text'
                      label={t('adminPanel.partners.addModal.otherEmr')}
                      variant='outlined'
                      error={!isNil(error)}
                      helperText={error ? error.message : null}
                      fullWidth
                      required
                    />
                  )}
                />
              )}
            </Box>

            <Typography
              variant='subtitle2'
              sx={{
                width: '100%',
                textAlign: 'left',
              }}
            >
              {t('adminPanel.partners.addModal.address')}
            </Typography>

            <FormGooglePlacesAutocomplete
              onChange={handleSelectLocation}
              required
            />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='street'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.partners.addModal.addressLine1')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    required
                    fullWidth
                  />
                )}
              />
              <Controller
                name='suite'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.partners.addModal.addressLine2')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    fullWidth
                  />
                )}
              />
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='city'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.partners.addModal.city')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    required
                    fullWidth
                  />
                )}
              />
              <Controller
                name='state'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <USStateSelect
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ''}
                    required
                  />
                )}
              />
            </Box>

            <Controller
              name='zip_code'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.partners.addModal.zip')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name='county'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.partners.addModal.county')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  fullWidth
                />
              )}
            />

            <Typography
              variant='subtitle2'
              sx={{ width: '100%', textAlign: 'left' }}
            >
              {t('adminPanel.partners.addModal.uploadWelcomeLetter')}
            </Typography>

            <Box sx={{ width: '100%' }}>
              <FileUploader
                file={file}
                setFile={handleChangeFile}
                acceptFormat='template'
              />
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                left: 0,
                backgroundColor: 'white',
                pb: 2,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting}
              >
                {t('general.ADD')}
              </Button>
            </Box>
          </Form>
        </FormProvider>
      </CustomModal>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </>
  );
};
